import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, Loader2, Check, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { value: "instant", label: "Instantly", hint: "Get notified the moment a match is listed" },
  { value: "daily", label: "Daily digest", hint: "One summary email each morning" },
  { value: "weekly", label: "Weekly digest", hint: "One summary email per week" },
];

/**
 * Save-search button + modal. Reusable across the marketplace and business
 * profile pages.
 *
 * Props:
 *   - filters: object passed straight to the backend (listing_type, min_price, etc.)
 *   - businessId: optional, scopes the search to one business listing's inventory
 *   - businessName: optional label shown in the modal
 *   - defaultName: optional auto-suggested search name (e.g. "3BR homes in Aurora")
 *   - variant: "primary" | "ghost"  (visual style of the trigger button)
 *   - testIdPrefix: optional, defaults to "save-search"
 */
export default function SaveSearchButton({
  filters = {},
  businessId,
  businessName,
  defaultName,
  variant = "primary",
  testIdPrefix = "save-search",
}) {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    name: defaultName || "",
    email: "",
    phone: "",
    frequency: "instant",
    channels: { email: true, sms: false },
  });

  // Prefill from logged-in profile
  useEffect(() => {
    if (!open) return;
    if (user) {
      setForm((f) => ({
        ...f,
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
        name: f.name || defaultName || "",
      }));
    } else {
      setForm((f) => ({ ...f, name: f.name || defaultName || "" }));
    }
  }, [open, user, defaultName]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email) {
      toast.error("Email is required so we know where to send alerts.");
      return;
    }
    const channels = [];
    if (form.channels.email) channels.push("email");
    if (form.channels.sms) channels.push("sms");
    if (channels.length === 0) {
      toast.error("Pick at least one channel (email or SMS).");
      return;
    }
    if (channels.includes("sms") && !form.phone) {
      toast.error("Phone is required for SMS alerts.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/v1/saved-searches`,
        {
          name: form.name || undefined,
          email: form.email,
          phone: form.phone || undefined,
          frequency: form.frequency,
          channels,
          filters,
          business_id: businessId || undefined,
        },
        isAuthenticated
          ? { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } }
          : undefined,
      );
      setSuccess(true);
      toast.success("Saved! We'll alert you when new matches go live.");
      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your search. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const triggerClass =
    variant === "ghost"
      ? "border border-white/20 text-white hover:bg-white/10"
      : "bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white hover:brightness-110";

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        className={`h-9 px-4 text-sm font-semibold ${triggerClass}`}
        data-testid={`${testIdPrefix}-trigger`}
      >
        <Bell className="w-4 h-4 mr-2" />
        Save Search & Alert Me
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !submitting && setOpen(false)}
            data-testid={`${testIdPrefix}-modal-overlay`}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              className="bg-[#0F172A] border border-white/10 rounded-2xl w-full max-w-md p-6 relative"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${testIdPrefix}-modal`}
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-white/60 hover:text-white"
                aria-label="Close"
                data-testid={`${testIdPrefix}-close`}
              >
                <X className="w-5 h-5" />
              </button>

              {success ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-7 h-7 text-green-400" />
                  </div>
                  <h3 className="text-white text-lg font-bold mb-1">Saved!</h3>
                  <p className="text-[#94A3B8] text-sm">We&apos;ll alert you when new matches appear.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-white text-lg font-bold mb-1 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-[#00E5FF]" />
                    Alert me on new matches
                  </h3>
                  <p className="text-[#94A3B8] text-sm mb-5">
                    {businessName
                      ? `We'll watch ${businessName}'s inventory and let you know when something new lists.`
                      : "We'll watch the marketplace for these filters and notify you as soon as new listings go live."}
                  </p>

                  <form onSubmit={submit} className="space-y-3">
                    <div>
                      <label className="text-xs text-[#94A3B8] mb-1 block">Search name</label>
                      <Input
                        placeholder="e.g. 3BR homes in Aurora under $500K"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid={`${testIdPrefix}-name`}
                      />
                    </div>

                    <div>
                      <label className="text-xs text-[#94A3B8] mb-1 block">Email *</label>
                      <Input
                        type="email"
                        required
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid={`${testIdPrefix}-email`}
                      />
                    </div>

                    <div>
                      <label className="text-xs text-[#94A3B8] mb-1 block">Phone (for SMS, optional)</label>
                      <Input
                        type="tel"
                        placeholder="+1 555 123 4567"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid={`${testIdPrefix}-phone`}
                      />
                    </div>

                    {/* Channels */}
                    <div>
                      <label className="text-xs text-[#94A3B8] mb-2 block">Send alerts via</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setForm({ ...form, channels: { ...form.channels, email: !form.channels.email } })
                          }
                          className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 border transition-colors ${
                            form.channels.email
                              ? "bg-[#00E5FF]/20 border-[#00E5FF]/50 text-white"
                              : "bg-white/5 border-white/10 text-[#94A3B8] hover:bg-white/10"
                          }`}
                          data-testid={`${testIdPrefix}-channel-email`}
                        >
                          <Mail className="w-4 h-4" /> Email
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setForm({ ...form, channels: { ...form.channels, sms: !form.channels.sms } })
                          }
                          className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 border transition-colors ${
                            form.channels.sms
                              ? "bg-[#7C3AED]/30 border-[#7C3AED]/60 text-white"
                              : "bg-white/5 border-white/10 text-[#94A3B8] hover:bg-white/10"
                          }`}
                          data-testid={`${testIdPrefix}-channel-sms`}
                        >
                          <MessageSquare className="w-4 h-4" /> SMS
                        </button>
                      </div>
                    </div>

                    {/* Frequency */}
                    <div>
                      <label className="text-xs text-[#94A3B8] mb-2 block">How often?</label>
                      <div className="space-y-2">
                        {FREQUENCIES.map((f) => (
                          <button
                            key={f.value}
                            type="button"
                            onClick={() => setForm({ ...form, frequency: f.value })}
                            className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                              form.frequency === f.value
                                ? "bg-white/10 border-[#00E5FF]/40"
                                : "bg-white/5 border-white/10 hover:bg-white/10"
                            }`}
                            data-testid={`${testIdPrefix}-freq-${f.value}`}
                          >
                            <p className="text-white text-sm font-medium">{f.label}</p>
                            <p className="text-[11px] text-[#94A3B8]">{f.hint}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-11 font-bold bg-gradient-to-r from-[#7C3AED] to-[#00E5FF]"
                      data-testid={`${testIdPrefix}-submit`}
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Bell className="w-4 h-4 mr-2" />
                      )}
                      Save & Alert Me
                    </Button>

                    <p className="text-[10px] text-[#64748B] text-center pt-1">
                      You can unsubscribe anytime from any alert email or your dashboard.
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
